import { stepRise } from './composer.js';
import { blastSquares, MINE_DAMAGE } from './tokens.js';
import { applyDamage } from './reducer.js';
import { hasActiveTurnOption } from './options.js';
import { reengineer } from './option-units.js';
/** A register holding exactly one card. */
export function slotOf(card) {
    return { cards: [card] };
}
/**
 * Replaces what a register PLAYS, keeping the gadget programmed into it.
 *
 * Five things rewrite a register after programming: the Abort Switch, a
 * Conditional swapped in at reveal, Radio Control copying a program over,
 * a Scrambler beam, and a randomizer square. None of them is equipment. A
 * launcher is hardware bolted to the robot, and nothing about drawing a
 * fresh card reaches into it.
 *
 * `slotOf` builds a slot from a card ALONE, so each of those sites
 * dropped the gadget along with the card it replaced -- a programmed
 * dropper that never fired, spent no ammo, and produced no event saying
 * why. The Abort Switch had this fixed inline and the other three had
 * not, which is why it lives here as one definition rather than four.
 *
 * The pairing Option (`via`) does NOT survive: the register now holds one
 * card, which is not a pair. The caller discards every card it replaced.
 *
 * A null card empties the register without disarming it: a register
 * carrying a gadget and no card is a legitimate play -- dropping
 * something and standing still -- and Radio Control copying an empty
 * register has to leave one.
 */
export function reprogram(slot, card) {
    const gadgets = slot?.gadgets;
    if (!gadgets || gadgets.length === 0)
        return card ? slotOf(card) : null;
    return { cards: card ? [card] : [], gadgets };
}
/**
 * Marks a locked register as written over before its reveal.
 *
 * See `RobotState.unseenLocks`. Called only for a register the
 * robot has a lock on: everything else is face down already.
 */
export function markUnseenLock(marks, register) {
    const next = (marks ?? [false, false, false, false, false]).slice();
    next[register] = true;
    return next;
}
/**
 * A register holding several cards, highest priority first.
 *
 * That ordering makes the highest-priority card the primary, which is the
 * usual rule. A caller that needs a different primary builds the slot
 * itself with that card first.
 */
export function slotOfMany(cards) {
    return { cards: [...cards].sort((a, b) => b.priority - a.priority) };
}
/**
 * The card that represents this register: its primary.
 *
 * Decides when the register runs, and is the one card taken when only one
 * can be -- copied by Radio Control, kept when a second card's Option is
 * lost.
 */
export function primaryCard(slot) {
    return slot?.cards[0] ?? null;
}
/** Every card in a register, in execution order. */
export function slotCards(slot) {
    return slot?.cards ?? [];
}
/**
 * The movers that are the BOARD moving a robot rather than the robot
 * moving itself.
 *
 * A proximity mine reaches a square further than the one it sits on, but
 * only against a robot that MOVES: "a conveyor conveying a robot past a
 * prox mine won't trigger it, but a robot pushed onto a prox mine or
 * conveyed ONTO a prox mine will". Contact with the device's own square
 * is unaffected; this governs the square around it only.
 *
 * A push is the robot moving: the shove comes from another robot's card
 * or a weapon, and the robot travels under it. A belt, a current and a
 * board pusher carry it.
 */
export const BOARD_MOVED = new Set([
    'conveyor', 'expressConveyor', 'current', 'pusher', 'gear',
]);
const DELTA = {
    N: [0, -1], S: [0, 1], E: [1, 0], W: [-1, 0],
};
export const OPPOSITE = { N: 'S', S: 'N', E: 'W', W: 'E' };
/**
 * The causes that are a PUSH, for the Hydraulic Bumpers.
 *
 * Everything that shoves a robot arrives here as one of these two: 'push'
 * carries a robot's own shove, a chain push, a pressor or tractor beam
 * and a repulsor field's throwback, and 'pusher' is the board element.
 * Conveyance is not a push -- a belt carries a robot, and sets its
 * coordinates without coming through `attemptOneSquare` at all.
 */
const PUSH_CAUSES = new Set(['push', 'pusher']);
export function neighborCoords(x, y, dir) {
    const [dx, dy] = DELTA[dir];
    return [x + dx, y + dy];
}
export function inBounds(grid, x, y) {
    return x >= 0 && y >= 0 && x < grid.width && y < grid.height;
}
/**
 * A square of the bounding box that no board covers.
 *
 * Not a pit and not a hazard: it is not there at all. Nothing stands on
 * it, nothing is dropped on it, no bridge spans it and no beam crosses
 * it.
 */
export function isVoid(cell) {
    return cell.floor?.kind === 'void';
}
/**
 * Whether this square is off the course: outside the bounding box, or
 * inside it but void.
 *
 * A SEPARATE predicate from `inBounds` on purpose. `inBounds` is called
 * 26 times and the calls do not all mean the same thing -- some ask "am I
 * off the course" (destroy), and some ask "is there a cell here to read"
 * (mirroring a wall, looking back for a cliff, tracing a beam's next
 * step). Redefining `inBounds` would silently change the second group,
 * and reading a void cell is perfectly safe. Only the first group moved.
 */
export function offCourse(grid, x, y) {
    return !inBounds(grid, x, y) || isVoid(grid.cells[y][x]);
}
function findCliff(features) {
    for (const f of features ?? []) {
        if (f.kind === 'cliff')
            return f;
    }
    return undefined;
}
/** Whether a robot holds Suction Cups with fuel left. */
export function canClimb(robot) {
    return (robot.options ?? []).some((o) => o.effect === 'suctionCups' && (o.remaining ?? 0) > 0);
}
/** Spends a fuel off a robot's Suction Cups. */
function spendSuctionCups(robot) {
    const cups = (robot.options ?? []).find((o) => o.effect === 'suctionCups' && (o.remaining ?? 0) > 0);
    if (!cups)
        return;
    robot.options = (robot.options ?? []).map((o) => (o.id === cups.id ? { ...o, remaining: (o.remaining ?? 0) - 1 } : o));
}
/**
 * Combined wall + cliff/ramp check for crossing the edge leaving `(x,y)` in
 * `dir`. A cliff may be recorded on either this cell's own edge or the
 * neighbor's reverse edge (per this project's "only one side records it"
 * convention) -- checked both ways, mirroring `composer.ts`'s
 * `elevationDelta`. A ridge has no high/low side: blocked unless a ramp is
 * present, never causes a fall, regardless of which direction it's crossed.
 */
export function getEdgeCrossing(grid, x, y, dir, 
/** The register being resolved. A bollard is only up on the registers
 * it lists, so without this it cannot be told whether it blocks.
 * Omitted means no bollard blocks -- which is what happened everywhere
 * until now, since nothing read them at all. */
register, 
/** Suction Cups: "Your robot may move up or down a ledge as normal
 * movement." A ledge with no ramp, either way, and a ridge, cost one
 * square and drop nobody. Walls still stop it. */
climbs = false) {
    const cell = grid.cells[y][x];
    const wallFeats = cell.edges[dir] ?? [];
    for (const f of wallFeats) {
        if (f.kind === 'wall' && f.oneWay !== 'green') {
            return { blocked: true, spiked: !!f.spikes, cost: 1, fallLevels: 0 };
        }
    }
    // A wall stops a robot from either side, but the board file may record
    // it on only one of them: 39 plain walls across the real boards are
    // written on a single side, so a robot approaching from the other
    // direction used to walk straight through.
    //
    // Only PLAIN walls are mirrored. A one-way wall is directional, and what
    // it means from the far side is not something to assume -- no one-way
    // wall in the real data is recorded one-sided, so nothing needs it.
    // Spikes are likewise not mirrored: they are on the face that carries
    // them.
    const [wx, wy] = neighborCoords(x, y, dir);
    if (inBounds(grid, wx, wy)) {
        const facing = grid.cells[wy][wx].edges[OPPOSITE[dir]] ?? [];
        if (facing.some((f) => f.kind === 'wall' && f.oneWay === undefined)) {
            return { blocked: true, spiked: false, cost: 1, fallLevels: 0 };
        }
    }
    // A bollard is a wall that is only up on certain registers. Checked on
    // BOTH sides of the gap: real board data records a pair facing each
    // other, and a bollard recorded on only one side must still block.
    if (register !== undefined) {
        const [bx, by] = neighborCoords(x, y, dir);
        const upHere = wallFeats
            .some((f) => f.kind === 'bollard' && f.phases?.includes(register));
        const upThere = inBounds(grid, bx, by)
            && (grid.cells[by][bx].edges[OPPOSITE[dir]] ?? [])
                .some((f) => f.kind === 'bollard' && f.phases?.includes(register));
        if (upHere || upThere) {
            return { blocked: true, spiked: false, cost: 1, fallLevels: 0 };
        }
    }
    const spiked = wallFeats.some((f) => f.kind === 'wall' && f.spikes);
    const [nx, ny] = neighborCoords(x, y, dir);
    // A stunt ramp cannot be entered over its EXIT edge -- that face is the
    // lip the robot flies off, not a way in. The other three sides are
    // open.
    //
    // `stuntRamp.entry` had been carried faithfully through the converter
    // and the composer and consulted by nothing but the elevation walk, so
    // a robot could drive in over the lip, and out over any side. Same shape
    // as the timed pits and the elevation: a field recorded everywhere and
    // read where it did not matter.
    //
    // Here rather than in the card path, so it governs a push, a belt and a
    // slide alike: the lip is a physical thing and does not care what is
    // doing the pushing.
    if (inBounds(grid, nx, ny)) {
        const enteringRamp = grid.cells[ny][nx].stuntRamp;
        if (enteringRamp && enteringRamp.exit === OPPOSITE[dir]) {
            return { blocked: true, spiked, cost: 1, fallLevels: 0 };
        }
    }
    const ownCliff = findCliff(cell.edges[dir]);
    let ridge = false;
    let ramp;
    let levels = 1;
    let sourceIsHigh;
    let foundCliff = false;
    if (ownCliff) {
        foundCliff = true;
        ridge = !!ownCliff.ridge;
        ramp = ownCliff.ramp;
        levels = ownCliff.levels ?? 1;
        sourceIsHigh = ownCliff.drop === 'out';
    }
    else if (inBounds(grid, nx, ny)) {
        const neighborCliff = findCliff(grid.cells[ny][nx].edges[OPPOSITE[dir]]);
        if (neighborCliff) {
            foundCliff = true;
            ridge = !!neighborCliff.ridge;
            ramp = neighborCliff.ramp;
            levels = neighborCliff.levels ?? 1;
            sourceIsHigh = neighborCliff.drop === 'in';
        }
    }
    if (!foundCliff) {
        return { blocked: false, spiked, cost: 1, fallLevels: 0 }; // no cliff feature at all
    }
    if (ridge) {
        // Confirmed earlier this session, no ramp exception: a ridge is
        // impassable in both directions, full stop -- a `ramp` field alongside
        // `ridge` (never seen in real data) would not make it crossable.
        // Suction Cups climb it.
        if (climbs)
            return { blocked: false, spiked, cost: 1, fallLevels: 0, climbed: true };
        return { blocked: true, spiked, cost: 1, fallLevels: 0 };
    }
    if (sourceIsHigh) {
        // Downhill. Correcting RULES_SPEC: going DOWN a ramp does no damage.
        // The board elements sheet states the 2-point fall for LEDGES, and
        // says only that "when a robot is moving down a ramp from the upper
        // level, the ramp has no effect on robot movement" -- no effect on
        // MOVEMENT, meaning no extra cost and no push back, not that the
        // robot falls. A ramp is the thing that makes the level change
        // traversable; walking down it is not a fall.
        //
        // Without a ramp it is an ordinary ledge and the fall stands.
        if (ramp)
            return { blocked: false, spiked, cost: 1, fallLevels: 0 };
        if (climbs)
            return { blocked: false, spiked, cost: 1, fallLevels: 0, climbed: true };
        return { blocked: false, spiked, cost: 1, fallLevels: levels };
    }
    // Uphill: blocked without a ramp, unless climbed.
    if (ramp)
        return { blocked: false, spiked, cost: 1 + ramp.extraMoves, fallLevels: 0 };
    if (climbs)
        return { blocked: false, spiked, cost: 1, fallLevels: 0, climbed: true };
    return { blocked: true, spiked, cost: 1, fallLevels: 0 };
}
// ============================================================
// Acting logs itself
// ============================================================
/**
 * Move a robot, and record the move.
 *
 * The recurring bug of this project, in its second form: a state change
 * and the decision to log it were two separate lines, so one happened
 * without the other. A shove changed the board and left no trace; a push
 * wrote two events for one movement; a laser hit recorded a count but not
 * who fired. Going through here makes the first two unrepresentable --
 * the event cannot be dropped because the caller never holds it, and
 * `from` cannot be wrong because it is read off the robot before the
 * write.
 *
 * Ordering is deliberately unchanged: this pushes exactly where the
 * hand-written literal used to sit.
 */
export function moveTo(mover, to, cause, events, opts = {}) {
    const from = opts.from ?? { x: mover.x, y: mover.y };
    mover.x = to.x;
    mover.y = to.y;
    const event = {
        type: opts.type ?? 'moved',
        robotId: mover.id,
        from,
        to: { x: to.x, y: to.y },
        facing: mover.facing,
        cause,
    };
    if (opts.card)
        event.card = opts.card;
    if (opts.terrain)
        event.terrain = opts.terrain;
    if (opts.count !== undefined)
        event.count = opts.count;
    events.push(event);
}
/**
 * Where a robot's own side ends up on the compass.
 *
 * The aimed Options are turned on the robot, so their side travels with
 * it. Left and right are the robot's, seen from inside it: facing north,
 * left is west.
 */
const CLOCKWISE = ['N', 'E', 'S', 'W'];
const QUARTERS = {
    front: 0, right: 1, back: 2, left: 3,
};
export function sideFacing(facing, relative) {
    return CLOCKWISE[(CLOCKWISE.indexOf(facing) + QUARTERS[relative]) % 4];
}
/**
 * The other way round: which side OF THE ROBOT a compass direction is.
 *
 * For the log. Every side the rules deal in is a compass direction --
 * that is what a beam and a push travel along -- but a player reads their
 * own robot, and "your front shield stopped it" says where to look in a
 * way that "your south shield" does not.
 */
export function relativeSide(facing, side) {
    const quarters = (CLOCKWISE.indexOf(side) - CLOCKWISE.indexOf(facing) + 4) % 4;
    return Object.keys(QUARTERS)
        .find((r) => QUARTERS[r] === quarters);
}
/**
 * The compass side an aimed Option covers on this robot right now, or
 * undefined if the card is not working.
 *
 * Two things leave it undefined, and they are the same answer to the
 * caller -- no protection on any side:
 *
 *   - the robot does not hold the card;
 *   - the robot is POWERED DOWN. An Option does nothing while a robot is
 *     powered down, and the Power Down Shield is the exception that
 *     proves it. A powered-down robot
 *     also never programmed, so it has nothing aimed either way.
 *
 * An Option the player holds but did not aim faces FRONT: that is the
 * card sitting square on the table, and it is what the client sends
 * nothing for.
 *
 * Inline rather than through `holdsOption`, as Ramming Gear and the
 * Suspension are: `options.ts` imports this module.
 */
export function aimedSideOf(robot, effect) {
    if (robot.poweredDown)
        return undefined;
    const card = (robot.options ?? []).find((o) => o.effect === effect);
    if (!card)
        return undefined;
    const aimed = (robot.aimedOptions ?? []).find((a) => a.optionId === card.id);
    return sideFacing(robot.facing, aimed?.facing ?? 'front');
}
/**
 * Which way this robot's MAIN LASER and OPTIONAL WEAPONS point.
 *
 * The Turret's whole effect: "your robot now has a turret for its main
 * laser and optional weapons [...] program the direction the turret
 * faces". Without the card, and for a robot that cannot use it, this is
 * simply the way the robot is facing.
 *
 * Not the robot's ADDITIONAL weapons -- the card says so, and the Rear
 * Laser and the Spiked Wheels are bolted to the chassis rather than to
 * the turret. Both name their own direction at the point they fire and
 * never come through here.
 */
export function weaponFacing(shooter) {
    return aimedSideOf(shooter, 'turret') ?? shooter.facing;
}
/**
 * A shield on one side of a robot, spent for the register.
 *
 * Two cards put one there and only one of them can ever be out at a time,
 * so they share this and share `shieldsSpent`:
 *
 *   - the Power Down Shield, while the robot is powered down. "A shield
 *     comes out on each side of the robot's four sides. Each shield
 *     protects the robot from 1 point of damage per register phase."
 *     Four INDEPENDENT shields of one point each: a robot hit from four
 *     sides in one register stops four points, and one hit four times
 *     from the same side stops one.
 *   - the Shield, while it is NOT. One shield, on the side the player
 *     aimed it at, stopping 1 point per register phase.
 *
 * Returns how much of `amount` gets through, spending the side's shield
 * where it stops a point, and the NAME of the card that stopped it so the
 * log can say which of the two it was. Callers that cannot say which side
 * the damage came from must not call this at all -- neither card blocks
 * "Radiation or any other damage which is non-directional", and a source
 * with no side is exactly that.
 *
 * Anything that arrives with a side is blocked, ranged and contact alike.
 */
export function shieldedFrom(robot, side, register, amount) {
    if (amount <= 0)
        return { through: amount };
    // Inline rather than through `holdsOption`, as Ramming Gear and the
    // Suspension are: `options.ts` imports this module.
    //
    // Powered down or not decides WHICH card is out, and never both: an
    // Option does nothing while a robot is powered down, and the Power Down
    // Shield is the exception written into the card.
    const card = robot.poweredDown
        ? (robot.options ?? []).find((o) => o.effect === 'powerDownShield')
        : (aimedSideOf(robot, 'shield') === side
            ? (robot.options ?? []).find((o) => o.effect === 'shield')
            : undefined);
    if (!card)
        return { through: amount };
    const spent = robot.shieldsSpent?.register === register
        ? robot.shieldsSpent.sides
        : [];
    if (spent.includes(side))
        return { through: amount };
    robot.shieldsSpent = { register, sides: [...spent, side] };
    return { through: amount - 1, name: card.name };
}
/**
 * The damage a Suspension softens: a fall off a ledge or cliff, and the
 * landing after a stunt ramp launch. Nothing else -- the card names the
 * three, and all three arrive as one of these two events.
 */
const FALL_DAMAGE = new Set(['fallDamage', 'rampLandingDamage']);
/**
 * Whether a robot is underwater and so out of the fight.
 *
 * "Your robot cannot be targeted, affected by weapons, or hit by flying
 * objects when in water. Your robot cannot fire its weapons when in
 * water." Both halves come from this one condition, which is why it is
 * a single predicate rather than two.
 *
 * Two things have to be true: the Submersible is switched on for the
 * turn, and the robot is standing in water RIGHT NOW. A submerged robot
 * that gets pushed out onto dry land is a target again in the same
 * register, and a robot that has the card on but is nowhere near water
 * is an ordinary robot.
 *
 * Deliberately NOT folded into `robotsInteract`, which is what a virtual
 * robot uses: that predicate also governs pushing, and "Your robot can
 * still be pushed" is explicit on the card. Submersion is a weapons
 * rule only.
 */
export function isSubmerged(grid, robot) {
    if (!hasActiveTurnOption(robot, 'submersible'))
        return false;
    const cell = grid.cells[robot.y]?.[robot.x];
    return cell !== undefined && hasTerrain(cell, 'water');
}
/**
 * Who is standing on a square, as far as a blast is concerned.
 *
 * The destroyed are not there, and neither is a submerged robot: "cannot
 * be affected by weapons" covers an explosion as well as a shot, so a
 * blast radiates past that square without finding anyone in it.
 *
 * One function because every explosion in the game asks the same
 * question -- a mine, a missile, a seismic charge -- and each used to ask
 * it in its own words.
 */
export function standingAt(grid, robots) {
    return (x, y) => {
        for (const r of robots) {
            if (r.destroyed || r.x !== x || r.y !== y)
                continue;
            return isSubmerged(grid, r) ? null : r.id;
        }
        return null;
    };
}
/**
 * Damage a robot, and record everything that can follow.
 *
 * A damage source used to be written out longhand at every site: push the
 * event naming the source, call `applyDamage` with a callback that pushes
 * the absorption, then push `destroyedByDamage` if it killed. Twenty
 * sites, and they did not agree. Five passed NO absorption callback, so an
 * Ablative Coat soaked the damage and the log never said so. Others
 * omitted the amount, which is how a laser hit came to report neither its
 * count nor its shooter.
 *
 * `spec` is the event naming the source -- its `type` plus whatever that
 * source carries, a square for spikes or a shooter for a laser. `robotId`
 * and `count` are filled in, so the amount can no longer go missing.
 *
 * Returns whether this killed the robot, so the caller can abandon
 * whatever it was doing, exactly as `applyDamage` did.
 *
 * Generic over the event array because the damage sites deal in four
 * different event unions -- move, laser, checkpoint and end of turn -- and
 * every one of them contains the types pushed here.
 */
export function hurt(mover, amount, events, spec, 
/**
 * Which side the damage arrives on, for callers that know. Omitted by
 * everything non-directional, which is what stops a Power Down Shield
 * blocking radiation or a fall.
 */
shield) {
    const robotId = mover.id;
    // A hit that arrives on a side says which, as a laser hit does: the
    // compass side of the victim it landed on.
    if (shield && spec.facing === undefined)
        spec = { ...spec, facing: shield.side };
    // The shield goes first: it is the outermost armour, and a point it
    // stops must not also be charged to the coat.
    //
    // A hit that reaches a shield is logged WHOLE, and the shield's line
    // follows it with what never landed -- the order laser fire uses, and
    // the one `resolveDebris` and the replay net the two by. `reported`
    // says the hit's own line is already out.
    let taken = amount;
    let reported = false;
    if (shield) {
        const { through, name } = shieldedFrom(mover, shield.side, shield.register, amount);
        if (through < taken) {
            events.push({ count: amount, ...spec, robotId });
            reported = true;
            events.push({
                type: 'damageShielded', robotId, count: taken - through,
                facing: shield.side, side: relativeSide(mover.facing, shield.side), name,
            });
            taken = through;
        }
    }
    // Suspension: "reduces damage received by 1 when falling off a ledge,
    // cliff, or stunt ramp". Every fall in the game reaches this function,
    // so taking the point off here covers the four places that charge one
    // -- driving over a ledge, sliding over one, being carried over one by
    // a board element, and landing off a stunt ramp -- without any of them
    // knowing the card exists.
    //
    // Tested inline rather than through `holdsOption`, as Ramming Gear is:
    // `options.ts` imports this module, so naming it here would close an
    // import cycle.
    //
    // It comes off BEFORE the Ablative Coat, so a fall the suspension
    // cancels outright does not also cost the coat a charge.
    //
    // Nothing while the robot is POWERED DOWN, as the coat is nothing: "an
    // Option does nothing while its robot is powered down", the Power Down
    // Shield excepted by its own card. A powered-down robot cannot drive
    // over a ledge, but it can very easily be SHOVED over one, which is the
    // case this guard is for.
    if (taken > 0 && !mover.poweredDown && FALL_DAMAGE.has(spec.type)) {
        const suspension = (mover.options ?? []).find((o) => o.effect === 'suspension');
        if (suspension) {
            taken -= 1;
            events.push({
                type: 'damageReduced', robotId, count: 1, name: suspension.name,
            });
        }
    }
    // Damage the suspension stops outright pushes no damage event at all. A
    // `fallDamage` of 0 would read as "falls and takes 0 damage" in the log,
    // and the debris rule counts these events. The `amount > 0` guard is
    // what keeps a caller's own 0 behaving as before; the shield and the
    // suspension have each already said what they did.
    if (taken === 0 && amount > 0)
        return false;
    // `count` before the spread, so a source that carries its own meaning
    // for the field -- or none -- still wins.
    if (!reported)
        events.push({ count: taken, ...spec, robotId });
    const killed = applyDamage(mover, taken, (absorbed, spent) => {
        events.push({ type: 'damageAbsorbed', robotId, count: absorbed });
        // A coat used up leaves play, and the event is its route to the
        // discard pile. See `applyDamage`.
        if (spent) {
            events.push({
                type: 'optionSpent', robotId, name: spent.name, optionId: spent.id,
            });
        }
    });
    if (killed)
        events.push({ type: 'destroyedByDamage', robotId });
    return killed;
}
/** Turn a robot, and record the turn. */
export function turnTo(mover, facing, cause, events, opts = {}) {
    mover.facing = facing;
    // A rotation happens in place, so `to` is always where the robot is
    // standing -- and it is what makes the turn a playback step rather than
    // a silent change of facing. Every rotation site already carried it.
    const event = {
        type: 'rotated', robotId: mover.id,
        to: { x: mover.x, y: mover.y }, facing, cause,
    };
    if (opts.turn)
        event.turn = opts.turn;
    if (opts.card)
        event.card = opts.card;
    events.push(event);
}
/**
 * Destroy a robot, and record why.
 *
 * The reason IS the event type, so there is no way to destroy a robot
 * without naming what did it.
 */
export function destroy(mover, reason, events) {
    mover.destroyed = true;
    events.push({ type: reason, robotId: mover.id });
}
/**
 * Whether Suction Cups save this robot from the pit it is in: fuel left,
 * and not a whirlpool. Off the board is not a pit, and never saved.
 */
export function pitSaves(grid, robot) {
    return canClimb(robot) && grid.cells[robot.y]?.[robot.x]?.floor?.whirlpool !== true;
}
/**
 * Suction Cups: "it will receive 2 points of damage and reappear at the
 * beginning of the next turn". A fuel and 2 damage now; a robot the
 * damage destroys is destroyed by it. Otherwise it is off the board until
 * End of Turn, held `destroyed` with `inPit` naming the pit.
 */
export function saveFromPit(robot, events) {
    spendSuctionCups(robot);
    events.push({ type: 'pitSaved', robotId: robot.id, at: { x: robot.x, y: robot.y } });
    if (hurt(robot, 2, events, { type: 'optionDamage', name: 'Suction Cups' }))
        return;
    robot.destroyed = true;
    robot.inPit = { x: robot.x, y: robot.y };
}
/** A robot standing in a pit falls in: destroyed, unless Suction Cups
 * save it. */
export function fallIntoPit(grid, robot, events) {
    if (pitSaves(grid, robot))
        saveFromPit(robot, events);
    else
        destroy(robot, 'destroyedPit', events);
}
export function isPit(cell) {
    return cell.floor?.kind === 'pit' || cell.floor?.kind === 'trapDoorPit';
}
export function robotAt(robots, x, y) {
    for (const r of robots.values()) {
        if (!r.destroyed && r.x === x && r.y === y)
            return r;
    }
    return undefined;
}
/**
 * EVERYONE standing at (x,y), which is not always one robot: a virtual
 * robot shares its square with the robot it came back on top of.
 *
 * For a source that acts on the SQUARE rather than picking a target out
 * of it -- a board laser's beam, a blast. `robotAt` answers with whichever
 * came first out of the map, so half of a shared square was silently
 * spared, and which half depended on the order the robots happened to be
 * in. The same fault `blockerAt` above exists to keep out of movement.
 */
export function robotsAt(robots, x, y) {
    return [...robots.values()].filter((r) => !r.destroyed && r.x === x && r.y === y);
}
/**
 * The order to apply a step that moves several robots at once, so that
 * nobody shoves a robot that is itself leaving.
 *
 * Everything moved by one of these steps moves TOGETHER, so a robot with a
 * move of its own is not standing in anybody's way. A robot standing on
 * another's path therefore goes FIRST, and the one behind follows it into
 * an empty square instead of pushing it along. The order is a topological
 * sort over "who is standing on whose path"; it comes out of the geometry
 * rather than out of the robots, which is the point.
 *
 * Applied in any flat order instead, whoever went first chain-pushed the
 * other, who then also took its own move and travelled two squares for
 * one shove. Both callers had this, and each broke the tie on something
 * arbitrary: the robots' IDs for a weapon's shove, the grid scan for a
 * pusher -- which made a line of pushers pointing east behave differently
 * from the same line pointing west.
 *
 * `cycled` is everyone the order cannot accommodate: two robots moved
 * into each other head-on, or a ring of them each moved into the next.
 * Whoever went first would be shoving a robot on its way out, so the
 * caller stops all of them -- the same answer, and the same reason, as
 * two robots converging on one square.
 *
 * Robots already stopped by some earlier rule must be left OUT of `moves`.
 * They are standing still, so a robot moved into one shoves it like any
 * other bystander.
 *
 * Roots are taken in ID order. Two moves with nothing between them do not
 * interact, so which runs first cannot change either answer; that is only
 * to keep the traversal itself repeatable.
 */
export function frontFirstOrder(moves, robots) {
    const ahead = new Map();
    for (const move of moves) {
        const mover = robots.get(move.id);
        const inTheWay = [];
        for (const square of move.path) {
            for (const other of moves) {
                if (other.id === move.id)
                    continue;
                const stood = robots.get(other.id);
                if (stood.x === square.x && stood.y === square.y && robotsInteract(mover, stood)) {
                    inTheWay.push(other.id);
                }
            }
        }
        ahead.set(move.id, inTheWay);
    }
    const order = [];
    const cycled = new Set();
    const state = new Map();
    const visit = (id, stack) => {
        const seen = state.get(id);
        if (seen === 'done')
            return;
        if (seen === 'visiting') {
            // Closes a cycle. Everyone from where it closes is in it.
            for (const member of stack.slice(stack.indexOf(id)))
                cycled.add(member);
            return;
        }
        state.set(id, 'visiting');
        for (const next of ahead.get(id) ?? [])
            visit(next, [...stack, id]);
        state.set(id, 'done');
        order.push(id);
    };
    for (const move of [...moves].sort((a, b) => a.id.localeCompare(b.id))) {
        visit(move.id, []);
    }
    return { order, cycled };
}
/**
 * The robot at (x,y) that would stop `mover`, if there is one.
 *
 * Not the same question as `robotAt`, which answers "who is standing
 * here". A virtual robot shares its square with a solid one, so two
 * robots can be on it at once, and `robotAt` returns whichever comes
 * first out of the map: with a virtual robot in front, the solid one
 * behind it was invisible, and a mover walked through it.
 *
 * Blocking is also not something a virtual robot does at all -- "movement
 * is not blocked by a virtual robot" -- and that holds whether the mover
 * is virtual or solid. Every caller asking "is my way barred" wants this
 * rather than `robotAt` plus a `robotsInteract` test it might forget: a
 * slide and a belt were both getting it wrong.
 */
export function blockerAt(robots, x, y, mover) {
    for (const r of robots.values()) {
        if (r.destroyed || r.x !== x || r.y !== y)
            continue;
        if (r.id === mover.id)
            continue;
        if (robotsInteract(mover, r))
            return r;
    }
    return undefined;
}
/**
 * Finds the other cell sharing this portal's colour, ON THE SAME BOARD.
 *
 * Portals belong to the board they are printed on. A yellow portal leads
 * to that board's other yellow portal, never to a yellow portal on a
 * different board in the same course.
 *
 * This has been wrong twice, each time by inferring the board from the
 * coordinates instead of asking. First it scanned the WHOLE composed grid,
 * which is right for a single-board course and wrong the moment two boards
 * carry the same portal colour -- a robot could be thrown across the
 * course to a board it had never been on. Then it scanned the aligned
 * 12-cell block the square falls in, which held only while every board sat
 * on a 12-cell lattice. Docking bays ended that: a 12x4 dock pushes the
 * boards past it over by four, so a board can span y=16..27 and a portal
 * near its bottom edge falls in the block starting at 24, along with a
 * slice of the board below.
 *
 * `boardKey` is the composed grid's own record of which board a cell came
 * from, which is exactly the question, so it is asked directly now and no
 * arithmetic can drift out from under it again.
 *
 * A grid whose cells carry no `boardKey` -- every synthetic test grid --
 * has every cell match every other, which is the original whole-grid scan
 * and correct for a single board.
 *
 * A portal whose pair is missing from its own board returns undefined, and
 * the caller leaves the robot where it is rather than moving it somewhere
 * arbitrary.
 */
export function findPortalPair(grid, colour, selfX, selfY) {
    const board = grid.cells[selfY]?.[selfX]?.boardKey;
    for (let y = 0; y < grid.height; y++) {
        for (let x = 0; x < grid.width; x++) {
            if (x === selfX && y === selfY)
                continue;
            const cell = grid.cells[y][x];
            if (cell.boardKey !== board)
                continue;
            if (cell.portal?.colour === colour)
                return { x, y };
        }
    }
    return undefined;
}
/**
 * Whether a repulsor field stands in the gap leaving (x,y) in `dir`.
 *
 * Checked on BOTH sides, as every other edge feature is: the board data
 * records a field on one face and the gap is the same gap from either
 * side of it.
 *
 * ONE copy. The card path and the slide path had written this out
 * separately, and conveyance needed a third once a belt or a current
 * running a robot into a field set it off too -- "moving into it is
 * moving into it".
 */
export function repulsorOnGap(grid, x, y, dir) {
    const [nx, ny] = neighborCoords(x, y, dir);
    const leaving = grid.cells[y][x].edges[dir] ?? [];
    const facing = inBounds(grid, nx, ny)
        ? (grid.cells[ny][nx].edges[OPPOSITE[dir]] ?? [])
        : [];
    return leaving.some((f) => f.kind === 'repulsor')
        || facing.some((f) => f.kind === 'repulsor');
}
/**
 * Where a portal on (x,y) would send this robot, or undefined if there is
 * no portal there or it cannot be taken.
 *
 * ONE copy, because every mover asks the same question and they had all
 * written their own version of it: a card, a push, a slide and a belt
 * each had the `findPortalPair` call and the occupancy test inline, and
 * the teleporter and the stunt ramp landing did not ask at all. "Any time
 * a robot lands on a portal it should portal" -- -- which only holds if
 * there is one place that decides it.
 *
 * A portal is refused by a robot on the far end only when the two can
 * actually collide. A VIRTUAL robot blocks nothing, so a far end holding
 * one is still open, and the two share the square exactly as they do
 * anywhere else.
 *
 * A portal whose pair is missing from its own board returns undefined and
 * the caller leaves the robot where it is, per RULES_SPEC section 3.
 */
export function portalExit(grid, robots, mover, x, y) {
    const portal = grid.cells[y]?.[x]?.portal;
    if (!portal)
        return undefined;
    const paired = findPortalPair(grid, portal.colour, x, y);
    if (!paired)
        return undefined;
    if (blockerAt(robots, paired.x, paired.y, mover))
        return undefined;
    return paired;
}
/**
 * Attempts to move a single robot exactly one square in `dir`, recursing
 * into a chain-push if the destination is occupied. Mutates `robots` and
 * appends to `events` only on an outcome that actually happens (a blocked
 * attempt never mutates position). Returns the outcome for this one robot.
 * Does not consider ramp COST -- that's the caller's job for a card-driven
 * mover (see `resolveRobotMove`); a pushed robot never pays it.
 */
/**
 * Whether two robots can affect one another.
 *
 * A virtual robot cannot affect other robots and cannot be affected by
 * them, including being shot, so two robots interact only when NEITHER is
 * virtual -- a virtual robot shares a square with a solid one, passes
 * through it, and neither pushes the other.
 *
 * This is what makes virtual mean anything. Before it, `virtual` was
 * recorded on respawn collisions and then honoured nowhere.
 */
/**
 * Whether a cell carries a terrain kind, wherever the converter recorded
 * it.
 *
 * Terrain lives in two places. Older board.json files list gravel, sand
 * and water in `terrain`; newer ones record them as the floor's `kind`.
 * Both forms are in the library at once, so every rule must check both --
 * reading `terrain` alone meant gravel and sand did nothing at all on
 * regenerated boards, and water did nothing on almost every board.
 *
 * The single place that knows this, so a future rule cannot get it wrong
 * by looking in only one of them.
 */
export function hasTerrain(cell, kind) {
    if (cell.terrain?.includes(kind))
        return true;
    return cell.floor?.kind === kind;
}
/**
 * How much damage a fall deals, given where it LANDS.
 *
 * A fall onto solid ground is 2 damage per level of elevation change; a
 * fall into water is 1. The landing square decides it, not the square
 * left behind.
 *
 * The amount used to be computed inline as `fallLevels * 2` and thrown
 * away rather than recorded on the event, so the log could only say a
 * robot "falls and takes damage" with no number, and the debris rule --
 * which counts an event's `count` and assumes 1 when absent -- scored a
 * 2-damage fall as 1 and left no wreckage behind.
 */
export function fallDamageAmount(grid, x, y, fallLevels) {
    if (!inBounds(grid, x, y))
        return fallLevels * 2;
    return fallLevels * fallDamagePerLevel(grid.cells[y][x]);
}
export function fallPastLanding(grid, robots, mover, x, y, dir, fell, vacating) {
    const inTheWay = (lx, ly) => {
        const there = blockerAt(robots, lx, ly, mover);
        return there && !vacating?.(there) ? there : undefined;
    };
    const blockedBy = inTheWay(x, y);
    if (!blockedBy)
        return undefined;
    let lx = x;
    let ly = y;
    // Height relative to the ledge it came off.
    let height = -fell;
    // Bounded by the board: a line of robots cannot be longer than that,
    // and one more step than that is off it whichever square it started on.
    for (let i = 0; i <= Math.max(grid.width, grid.height); i++) {
        const count = Math.abs(lx - x) + Math.abs(ly - y);
        const fallLevels = Math.max(0, -height);
        if (offCourse(grid, lx, ly)) {
            return { x: lx, y: ly, count, blockedBy, offBoard: true, bounced: false, fallLevels };
        }
        if (!inTheWay(lx, ly)) {
            return { x: lx, y: ly, count, blockedBy, offBoard: false, bounced: false, fallLevels };
        }
        // The way on is barred. A falling robot with a robot beneath it and a
        // wall beyond has nowhere to come down, so it BOUNCES BACK to the
        // ledge it fell off (ruled 2026-09-10) -- the one square that is
        // certainly clear, since the robot was standing on it a moment ago.
        //
        // A ledge back up is not a bar when it leaves the robot no higher
        // than where it started: it keeps moving, over the robot below.
        const rise = stepRise(grid, lx, ly, dir);
        const overTheTop = rise > 0 && height + rise <= 0
            && !getEdgeCrossing(grid, lx, ly, dir, undefined, true).blocked;
        if (getEdgeCrossing(grid, lx, ly, dir).blocked && !overTheTop) {
            return { x: lx, y: ly, count, blockedBy, offBoard: false, bounced: true, fallLevels: 0 };
        }
        height += rise;
        [lx, ly] = neighborCoords(lx, ly, dir);
    }
    return { x: lx, y: ly, count: 0, blockedBy, offBoard: true, bounced: false, fallLevels: 0 };
}
/**
 * What one level of a fall costs, given the square landed on.
 *
 * Split out so a landing this module does not own answers the same way:
 * `rampExitFallDamage` had its own `levelsFallen * 2` and no water test
 * at all, so a stunt ramp dropped a robot into water for 2 where a ledge
 * beside it cost 1. Water is a property of the LANDING SQUARE, not of the
 * thing that dropped the robot on it.
 */
export function fallDamagePerLevel(cell) {
    return hasTerrain(cell, 'water') ? 1 : 2;
}
/**
 * Every terrain kind on a cell, from both places it can be recorded.
 *
 * For callers that need the whole list rather than to ask about one kind
 * -- `adjustForStartingTerrain` takes an array, and was being handed only
 * `cell.terrain`, so a Move 3 starting on a floor-kind sand square was
 * not restricted at all.
 */
export function terrainsOf(cell) {
    const listed = cell.terrain ?? [];
    const floor = cell.floor?.kind;
    // `void` is in this list for the same reason `pit` is: it is a floor
    // KIND that is not a terrain. It should never be reached -- a robot
    // that enters void is destroyed before anything asks what it is
    // standing on -- but leaving it out would put the string "void" into a
    // terrain array, where every consumer would treat it as an unknown
    // terrain and quietly do nothing. The compiler caught this one.
    if (!floor || floor === 'open' || floor === 'pit' || floor === 'trapDoorPit'
        || floor === 'void') {
        return listed;
    }
    return listed.includes(floor) ? listed : [...listed, floor];
}
/** A ledge crossed on Suction Cups: the fuel it costs, and the log line. */
function climbedBy(mover, events) {
    spendSuctionCups(mover);
    events.push({ type: 'suctionCupsClimbed', robotId: mover.id });
}
/** Squares of attempted movement it takes to break out of goo. */
export const GOO_STRUGGLE = 4;
export function robotsInteract(a, b) {
    return !a.virtual && !b.virtual;
}
export function attemptOneSquare(grid, robots, robotId, dir, events, 
/** What is doing the moving. Recorded on the event so the log can say
 * whether a robot walked, was shoved, or rode something. */
cause = 'card', 
/** The register being resolved. A flamethrower only burns on the
 * registers it is lit for, so entering one is harmless otherwise.
 * Omitted by Board Elements Move, which is not a time flamethrowers
 * fire, and by callers outside a register resolution. */
register, 
/** Squares the ORIGINATING card was worth. A speed bump damages a robot
 * that arrives under a Move 2 or Move 3, and a pushed robot is damaged
 * by the card that pushed it, so this is passed down a chain rather
 * than recomputed. Undefined where no card is responsible. */
moveValue, 
/**
 * Repulsor sides that have already fired this register.
 *
 * "Each repulsor field will activate only once per register phase per
 * side", so this must live for a whole register and be shared by every
 * move within it -- which is why it is threaded rather than rebuilt.
 * Keyed by the square and direction of APPROACH, so the two sides of
 * one field are separate entries.
 */
repulsorsFired, 
/**
 * How many squares of push each robot has already spent climbing each
 * ramp this register.
 *
 * A push of TWO up a ramp spends its first square getting up and moves
 * on the second, so a ramp costs a push exactly what it costs a card. A
 * single push therefore achieves nothing, and the pusher does not move
 * either.
 *
 * Threaded rather than stored on the robot because it is per-register
 * scratch, like `repulsorsFired`. Keyed by robot, square and direction:
 * progress against one ramp is not progress against another.
 */
rampPushProgress, 
/**
 * Ramps already paid for during this card's movement, keyed by the gap.
 *
 * "the pusher doesn't pay the cost of the ramp twice." The squares
 * spent shoving a robot up a ramp carry the pusher over it too, so once
 * a chain has paid the climb, the same gap costs one square to anyone
 * following in that movement.
 */
rampPaid, 
/**
 * Robots already rammed during this card's movement.
 *
 * Ramming Gear is once per CARD, not once per square, so a Move 3 that
 * shoves the same robot three squares deals one point, not three.
 */
rammed, 
/**
 * Robots whose Hydraulic Bumpers have already absorbed a square of THIS
 * push.
 *
 * "When pushed from that direction, your robot will reduce the distance
 * pushed by 1 square" -- one square off the push, not off every square
 * of it, so a shove of three from the bumpered side moves two.
 *
 * Same scope as `rammed`: one card's movement, or one beam's shove.
 * A caller with only ever a single square to give -- a board pusher --
 * passes none and absorbs it, which is the same answer.
 */
bumped, 
/**
 * The register to read PHASE-GATED EDGES for, when that is not the same
 * register that effects fire on.
 *
 * The two differ in exactly one place: Board Elements Move. A board
 * pusher is not a time flamethrowers fire, so it passes no `register`
 * -- and that also hid every raised BOLLARD from it, because
 * `getEdgeCrossing` reads them off the same argument. A bollard is a
 * wall while it is up and stops a pusher like any other wall, so the
 * pusher passes the register here and leaves `register` empty.
 *
 * Defaults to `register`, which is what every other caller wants.
 */
edgeRegister = register, 
/** The dropped devices on the board, spent as they cut. Threaded like
 * `repulsorsFired` above and for the same reason: it is per-step state
 * that every mover shares, not a property of the board. */
devices, 
/** Pit Move or Shunt, on a push this card makes. */
pushWith, 
/** A Reengineering Unit's exchange, made at the push. */
reengineerWith) {
    const mover = robots.get(robotId);
    if (!mover || mover.destroyed)
        return 'blocked';
    // Suction Cups work under the robot's own power only.
    const edge = getEdgeCrossing(grid, mover.x, mover.y, dir, edgeRegister, cause === 'card' && canClimb(mover));
    // Spikes are not directional, so no shield stops them.
    if (edge.spiked) {
        if (hurt(mover, 1, events, { type: 'spikeDamage' }))
            return 'destroyed';
    }
    if (edge.blocked) {
        events.push({ type: 'blocked', robotId });
        return 'blocked';
    }
    // A ramp costs a PUSH exactly what it costs a card. "it goes up 1
    // (virtual) square and then back to its starting space" -- so one
    // square of push achieves nothing -- "unless a robot is pushed TWO up a
    // ramp, then it will use one space of push to go up the ramp but not
    // move, and then on the second push it will go up".
    //
    // 'held' rather than 'blocked': a square was consumed and nothing
    // moved, but the push is not over. The same shape as goo, which the
    // move loop already treats as an exception to "blocked ends the move".
    const gap = `${mover.x},${mover.y},${dir}`;
    if (cause === 'push' && edge.cost > 1 && !rampPaid?.has(gap)) {
        const key = `${robotId}:${gap}`;
        const spent = (rampPushProgress?.get(key) ?? 0) + 1;
        if (spent < edge.cost) {
            rampPushProgress?.set(key, spent);
            events.push({ type: 'rampPushHeld', robotId, count: edge.cost - spent });
            return 'held';
        }
        rampPushProgress?.delete(key);
        // Paid. Anyone following the pushed robot over this same gap during
        // this movement crosses for one square.
        rampPaid?.add(gap);
    }
    // Hydraulic Bumpers: "when pushed from that direction, your robot will
    // reduce the distance pushed by 1 square". to cover ANY push -- a
    // robot's shove, a board pusher, a pressor beam, a repulsor field's
    // throwback -- so a one-square push from the bumpered side is cancelled
    // outright.
    //
    // The push comes from behind, so it arrives on the side facing back
    // down it: a robot shoved north is hit on its south side.
    //
    // 'held' rather than 'blocked', exactly as the ramp above: a square was
    // consumed and nothing moved, but the push is not over, and the next
    // square of it moves the robot.
    if (PUSH_CAUSES.has(cause) && !bumped?.has(robotId)
        && aimedSideOf(mover, 'hydraulicBumpers') === OPPOSITE[dir]) {
        bumped?.add(robotId);
        events.push({ type: 'bumpersHeld', robotId });
        return 'held';
    }
    // A BOARD pusher cannot push a robot up a ramp at all. It shoves one
    // square and has no second square to spend, so there is no progress to
    // accumulate.
    if (cause === 'pusher' && edge.cost > 1) {
        events.push({ type: 'blocked', robotId });
        return 'blocked';
    }
    // NOTE: the "conveyance cannot carry a robot UP a ramp" rule used to sit
    // here, gated on `cause === 'conveyor'`, where it could never fire --
    // conveyance sets coordinates directly and never calls this function.
    // It now lives in `resolveConveyance`, which is where the decision is
    // actually made, and covers currents and express belts as well.
    let [nx, ny] = neighborCoords(mover.x, mover.y, dir);
    // Goo. "If a robot passes over or stops on the goo, the robot cannot
    // leave that square until it attempts to move a total of four squares in
    // any direction." Every attempted square counts, including being pushed,
    // and rotating is free -- a rotate never reaches this code.
    //
    // `stuckIn.attempted` is the working copy of a count that lives on the
    // TOKEN; `resolveGoo` seeds it and writes it back. See `Token.struggled`.
    //
    // This block now serves PUSHES only -- a shove, a pusher, a repulsor
    // throwback, a fall. A card no longer reaches it: `moveRobot` struggles
    // above its own cost test, so that a ramp too dear to climb still
    // spends a square. Do not fold the two together: a push has no budget
    // of its own to charge.
    if (mover.stuckIn) {
        const attempted = mover.stuckIn.attempted + 1;
        if (attempted < GOO_STRUGGLE) {
            mover.stuckIn = { ...mover.stuckIn, attempted };
            events.push({
                type: 'stuckInGoo', robotId, tokenId: mover.stuckIn.tokenId, count: GOO_STRUGGLE - attempted,
            });
            return 'blocked';
        }
        // Free, and the goo is destroyed behind it -- but the square that
        // broke it free is SPENT on breaking free, not on travelling. Only
        // that square: on a CARD the rest of the budget then travels
        // normally, which the loop in `resolveRobotMove` handles.
        //
        // Here, on a PUSH, one square is all there is, so 'blocked' is the
        // whole answer: this shove freed the goo and shifted nobody. A second
        // square of the same push would move the robot, since the goo is gone
        // by then.
        events.push({
            type: 'brokeFreeOfGoo', robotId, tokenId: mover.stuckIn.tokenId, at: { x: mover.x, y: mover.y },
        });
        mover.stuckIn = undefined;
        return 'blocked';
    }
    // Repulsor field. A robot that runs into one is pushed directly away
    // for as many squares as its movement card is worth, and loses the rest
    // of that card's movement. A robot pushed into one is thrown back by
    // the PUSHING robot's card value, which is what `moveValue` already
    // carries down a chain. A robot thrown back can push others, so the
    // ordinary push machinery is used for it.
    if (repulsorsFired !== undefined) {
        const key = `${mover.x},${mover.y},${dir}`;
        const hasField = repulsorOnGap(grid, mover.x, mover.y, dir);
        // A field already spent this register is a WALL, not an absence. It
        // used to fall straight through to the ordinary edge checks, so a
        // spent field simply was not there: the robot that tripped it could
        // walk back through later in the same register, and any OTHER robot
        // crossing the same gap the same way got a free pass, because the key
        // is the gap and the direction rather than the robot.
        if (hasField && repulsorsFired.has(key)) {
            events.push({ type: 'blocked', robotId });
            return 'blocked';
        }
        if (hasField) {
            repulsorsFired.add(key);
            const away = OPPOSITE[dir];
            const throwback = moveValue ?? 1;
            // One throwback is one push, so the bumpers take one square off the
            // whole of it rather than off each square.
            const thrownBumped = new Set();
            // And one throwback is one action, so its squares add up against a
            // ramp at the foot of the field: a throw of two gets a robot up one.
            const thrownUp = new Map();
            for (let i = 0; i < throwback; i++) {
                const result = attemptOneSquare(grid, robots, robotId, away, events, 'push', register, moveValue, repulsorsFired, thrownUp, undefined, undefined, thrownBumped, edgeRegister, devices);
                // 'held' is a square spent on the bumpers or a ramp with nothing
                // moved: the throw is not over and the next square still goes.
                if (result !== 'moved' && result !== 'held')
                    break;
            }
            events.push({
                type: 'repulsed', robotId,
                to: { x: mover.x, y: mover.y }, facing: mover.facing, cause: 'repulsor',
            });
            // The rest of the card's movement is lost.
            return 'blocked';
        }
    }
    // A virtual robot is passed through rather than pushed, and a virtual
    // mover pushes nobody.
    const occupant = blockerAt(robots, nx, ny, mover);
    // A robot falling off a ledge does not push whoever is below it. From
    // the rulebook: "If this would result in them landing on a square
    // occupied by another robot, they land in the next open square
    // determined by their direction of movement." It is falling, not
    // driving, so it slides past rather than shoving.
    //
    // The search runs in the direction of travel. Running out of board, or
    // finding nothing open, is handled by the ordinary checks below on
    // whatever square it settles on.
    let fallLevels = edge.fallLevels;
    if (occupant && edge.fallLevels > 0) {
        const landing = fallPastLanding(grid, robots, mover, nx, ny, dir, edge.fallLevels);
        if (landing?.offBoard) {
            // Nowhere to land in that direction: off the board.
            destroy(mover, 'destroyedOffBoard', events);
            return 'destroyed';
        }
        if (landing?.bounced) {
            // A wall beyond the robot below, so there is nowhere to come down:
            // back onto the ledge, which is where the robot already is. No
            // move, so no fall damage -- it never landed anywhere lower.
            events.push({
                type: 'bouncedBack',
                robotId,
                targetId: landing.blockedBy.id,
                to: { x: landing.x, y: landing.y },
            });
            return 'blocked';
        }
        if (landing && landing.count > 0) {
            // This happened in silence. The robot landed a square or two beyond
            // the ledge it came off, with nothing in the log between the fall
            // and the landing to account for the difference -- it read as the
            // fall having gone further than it did.
            events.push({
                type: 'fellPast',
                robotId,
                targetId: landing.blockedBy.id,
                count: landing.count,
                to: { x: landing.x, y: landing.y },
            });
            nx = landing.x;
            ny = landing.y;
            fallLevels = landing.fallLevels;
        }
    }
    else if (occupant) {
        // Ramming Gear: "When your robot pushes another robot, that robot
        // receives a point of damage in addition to being pushed. Even if the
        // target robot can't be moved, it still receives a point of damage."
        //
        // Charged BEFORE the push is attempted, because the second sentence
        // means the outcome does not matter: a robot shoved against a wall,
        // or held on a ramp, is damaged just the same.
        //
        // Two limits, both:
        //   - ONLY under the holder's own card. A robot that is itself being
        //     shoved and passes the push along does not ram, which is what
        //     `cause === 'card'` tests.
        //   - Once per CARD per victim, not once per square, so a Move 3 that
        //     shoves the same robot three squares deals one point.
        //
        // Tested inline rather than via `holdsOption`: `options.ts` imports
        // `ProgramCard` from here, so a VALUE import back would be the first
        // real cycle between the two. The type import already here is fine.
        if (cause === 'card'
            && !rammed?.has(occupant.id)
            && (mover.options ?? []).some((o) => o.effect === 'rammingGear')) {
            rammed?.add(occupant.id);
            // The pusher comes from behind, so the ram lands on the side of the
            // victim facing back down the push.
            hurt(occupant, 1, events, { type: 'rammingGearHit' }, edgeRegister === undefined
                ? undefined
                : { side: OPPOSITE[dir], register: edgeRegister });
        }
        // Reengineering Unit: the same push, and the exchange made before it,
        // so wherever the push sends the robot it has already given up its
        // Option. After the ram, so a Ramming Gear just taken does not ram the
        // robot it came from, and a robot the ram destroyed gives up nothing.
        // Marked once per card per robot pushed, as Ramming Gear, so the
        // question before the card can find who would be pushed.
        if (cause === 'card'
            && (mover.options ?? []).some((o) => o.effect === 'reengineeringUnit')
            && !events.some((e) => e.type === 'reengineerContact'
                && e.robotId === occupant.id && e.sourceId === mover.id)) {
            events.push({ type: 'reengineerContact', robotId: occupant.id, sourceId: mover.id });
            const live = reengineerWith && !reengineerWith.done ? reengineerWith : undefined;
            const optionId = live && !occupant.destroyed ? live.pick(occupant) : undefined;
            // Turn mode's chance is the register's first push, taken or not.
            if (live?.firstOnly)
                live.done = true;
            const done = optionId === undefined ? undefined : reengineer([mover, occupant], mover.id, occupant.id, optionId);
            if (done) {
                live.done = true;
                mover.options = done.robots[0].options;
                occupant.options = done.robots[1].options;
                events.push({
                    type: 'reengineered', robotId: mover.id, targetId: occupant.id,
                    name: done.taken[0].name, ...(done.taken[1] ? { withName: done.taken[1].name } : {}),
                });
            }
        }
        // Shunt: "push a robot 1 square to the right (or left) instead of
        // pushing it forward", across the way this robot is moving. A real
        // push, so it chains into a robot beside the target. The square ahead
        // is then clear and this robot carries on; a target that stays put
        // stops it, as any push does.
        const shunt = cause === 'card' && pushWith?.effect === 'shunt' ? pushWith.side : undefined;
        const pushDir = shunt ? sideFacing(dir, shunt) : dir;
        if (shunt)
            events.push({ type: 'shunted', robotId: occupant.id, sourceId: mover.id, side: shunt });
        const pushedFrom = events.length;
        const pushResult = attemptOneSquare(grid, robots, occupant.id, pushDir, events, 'push', register, moveValue, repulsorsFired, rampPushProgress, rampPaid, rammed, bumped, edgeRegister, devices);
        // The occupant's own moves under this push name who shoved it. Anyone
        // it shoved in turn is named by the level below, as pushed by it.
        for (let i = pushedFrom; i < events.length; i++) {
            const e = events[i];
            if (e.type === 'moved' && e.cause === 'push' && e.robotId === occupant.id && !e.sourceId) {
                events[i] = { ...e, sourceId: mover.id };
            }
        }
        // Pit Move: "force the other robot to immediately U-Turn (even if the
        // target robot was not moved)". Once a card, as Ramming Gear: a Move 3
        // shoving one robot three squares turns it once.
        if (cause === 'card' && pushWith?.effect === 'pitMove' && !pushWith.done.has(occupant.id)
            && !occupant.destroyed) {
            pushWith.done.add(occupant.id);
            events.push({ type: 'pitMoveHit', robotId: occupant.id, sourceId: mover.id });
            turnTo(occupant, OPPOSITE[occupant.facing], 'push', events, { turn: 'about' });
        }
        // The occupant spent a square climbing and did not move, so neither
        // does this robot -- but the push is not over, and the caller must
        // keep spending so a second square can finish the climb.
        if (pushResult === 'held')
            return 'held';
        if (pushResult === 'blocked') {
            events.push({ type: 'blocked', robotId });
            return 'blocked';
        }
        // NOTE: no `pushed` event. The recursive call above has already
        // emitted a `moved` with cause 'push', carrying a `from` as well as a
        // `to`, so a second event said the same thing worse -- the log read
        // "rick was pushed from 8,2 to 9,2" and then "rick was pushed to
        // 9,2", and the playback animated a move to the square the robot was
        // already standing in. Both consumers of `pushed` accept `moved`.
        //
        // The event type and its renderers stay: log rows already written
        // contain `pushed` and still have to display.
        // occupant has now moved or been destroyed; (nx,ny) is vacant, fall
        // through to the ordinary destination checks below.
    }
    // NOTE: fall damage is NOT applied here, before the move. It used to
    // be, and that was wrong in three visible ways at once: a lethal fall
    // returned from this function before the robot was ever moved, so it
    // died in the square it started in, the debris was left in that square
    // rather than where it landed, and no `moved` event was emitted at all
    // so the playback could not show the fall. It is applied on arrival
    // instead, below the `moved` event.
    if (offCourse(grid, nx, ny)) {
        destroy(mover, 'destroyedOffBoard', events);
        return 'destroyed';
    }
    const destCell = grid.cells[ny][nx];
    if (isPit(destCell)) {
        // The robot falls IN, so it is moved onto the pit square and destroyed
        // there rather than destroyed in the square it stepped out of. Without
        // the move there is no `moved` event, the playback never sees the
        // robot leave, and the client draws the wreck on the last square it
        // knew about -- reported as a robot dying down a mine shaft and the
        // cross appearing on the square beside it.
        //
        // Same shape as the fall-damage note above, and the stunt ramp's
        // landing in `orchestration.ts` already did it this way round.
        // No debris follows: `destroyedPit` is deliberately not in
        // `DESTRUCTIONS` in `game.ts`, because a robot down a pit is not
        // there any more and neither are its pieces.
        if (edge.climbed)
            climbedBy(mover, events);
        moveTo(mover, { x: nx, y: ny }, cause, events);
        fallIntoPit(grid, mover, events);
        return 'destroyed';
    }
    if (destCell.portal) {
        const paired = portalExit(grid, robots, mover, nx, ny);
        if (paired) {
            // TWO events, not one. The robot steps onto the portal square and
            // is then relocated, and both are worth showing: reporting only the
            // relocation, from the square the robot started on, made a move
            // east followed by a portal south look like a single diagonal jump
            // that never touched the portal at all.
            moveTo(mover, { x: nx, y: ny }, cause, events);
            // BOTH ENDS have their say. The robot entered the portal square and
            // then entered the square it emerged on, so a spiked floor at
            // either end bites and one at both ends bites twice.
            //
            // This was a KNOWN GAP, left unfixed on the grounds that no board
            // should put a portal under a flamethrower: the function returned
            // here and applied nothing at either end. The ruling closes it, and
            // the flamethrower comes along with the rest.
            const entered = applyArrivalEffects(grid, mover, events, register, moveValue, devices, cause, robots, edgeRegister);
            if (entered === 'destroyed')
                return 'destroyed';
            moveTo(mover, paired, 'portal', events, {
                type: 'portaled', from: { x: nx, y: ny },
            });
            const emerged = applyArrivalEffects(grid, mover, events, register, moveValue, devices, cause, robots, edgeRegister);
            if (emerged === 'destroyed')
                return 'destroyed';
            // Caught by goo at either end ends the move, exactly as driving
            // into goo does.
            return emerged === 'stuck' || entered === 'stuck' ? 'stuckHere' : 'moved';
        }
        // paired destination missing or occupied -- inert, fall through as
        // an ordinary square per RULES_SPEC section 3.
    }
    if (edge.climbed)
        climbedBy(mover, events);
    moveTo(mover, { x: nx, y: ny }, cause, events);
    // The fall is resolved HERE, once the robot is standing in the square
    // it fell into. The move is shown first, then the damage, which is the
    // order a player watching the board expects and the order the debris
    // rule needs: a robot destroyed by the fall leaves its wreckage where
    // it landed.
    //
    // The amount is carried on the event so the log can name it, and the
    // absorption callback is passed like every other damage site so an
    // Ablative Coat soaks up a fall as it soaks up anything else.
    //
    // A fall ENDS that robot's movement for the register, surviving it or
    // not. That is recorded in `fellHere` and returned at the BOTTOM of
    // this function, not here. Returning on the spot skipped everything the
    // landing square does -- the `enteredSquare` event above all, which is
    // what sets off mines and debris -- so a robot that dropped onto a
    // mine took the fall and nothing else.
    let fellHere = false;
    if (fallLevels > 0) {
        const fall = fallDamageAmount(grid, nx, ny, fallLevels);
        if (hurt(mover, fall, events, { type: 'fallDamage' }))
            return 'destroyed';
        fellHere = true;
    }
    // A flamethrower burns a robot that MOVES INTO it, and burns again
    // during Resolve Laser Fire, so a robot that drives in and stays takes
    // two, and one that was already standing there takes only the
    // laser-fire one. This is the entry half; the other is in
    // laser-fire.ts.
    //
    // ONLY Robot Movement counts. Board Elements Move -- belts, pushers,
    // gears -- is explicitly NOT a time flamethrowers fire, so a robot
    // carried onto one by a conveyor is not burned for the ride. Those
    // callers pass no register, which is what switches this off.
    //
    // Within Robot Movement it applies however the robot got there: its own
    // card, or being shoved by another robot's move. Each lit square
    // entered burns, so a Move 3 across three of them costs three.
    // NOTE: being pushed off a stunt ramp's exit side needs no code here.
    // The manual gives it "2 points of damage per level of elevation
    // change", and the boards already encode that as an ordinary cliff on
    // the square below -- RollingHills' 0,2 carries a north-edge cliff with
    // `drop: in`, so the existing fall handling deals exactly 2. Adding a
    // second rule here double-counted it.
    const arrival = applyArrivalEffects(grid, mover, events, register, moveValue, devices, cause, robots, edgeRegister);
    if (arrival === 'destroyed')
        return 'destroyed';
    // The landing square has had its say; now the fall ends the move.
    // 'fell' is distinct from 'blocked', which means the robot never moved:
    // it did move, one square, and then the move is over.
    if (fellHere)
        return 'fell';
    // Goo caught it on arrival. It DID move -- the square counts, and the
    // robot is standing in the goo -- but it goes no further under this
    // card. A separate result from 'fell' because nothing else about a fall
    // applies.
    return arrival === 'stuck' ? 'stuckHere' : 'moved';
}
/**
 * Sets off whatever the robot is standing on, and lets the blast go.
 *
 * The one copy of the rule. Every mover reaches it: a card, a push, a
 * portal or teleporter exit, a stunt ramp landing, a belt or a pusher
 * delivery, a slide, a rotate in place. Each of them used to keep its
 * own version, and the three that existed disagreed about the amount on
 * the event and about whether a raised bollard sheltered anyone.
 *
 * The robot must ALREADY be standing in the square, the same contract
 * `applyArrivalEffects` has. Returns whether the MOVER died of it, which
 * is what ends its movement here; a bystander the blast kills is left for
 * the caller to notice like any other casualty.
 */
export function detonateSteppedOn(grid, mover, events, 
/** The dropped devices on the board. Absent from a caller with no token
 * layer to spend from -- the tests, mostly. */
devices, 
/** Everyone on the board. A blast catches whoever is near it, so the
 * bystanders have to be reachable from here. Without them the trigger is
 * still recorded and spent, but nothing goes off. */
robots, 
/** Whether a proximity mine a square away senses this. Contact with the
 * device's own square always tells; the square around it answers only to
 * a robot MOVING under its own power. See `BOARD_MOVED`. */
sensed, 
/** The register being resolved, so a raised bollard shelters whoever is
 * behind it -- a bollard that is up is a wall. */
register) {
    const bystanders = robots === undefined ? undefined : [...robots];
    const blown = devices?.steppedOn(mover.x, mover.y, mover.id, sensed, bystanders && { grid, register, occupied: standingAt(grid, bystanders) });
    let moverDied = false;
    for (const hit of blown ?? []) {
        // Said out loud once per device, so the log has the bang as well as
        // the damage -- and says so even when it caught nobody. The amount is
        // what the device DOES, not what this victim took: the halving is the
        // distance, and the line names the explosion.
        const at = { x: hit.source.x, y: hit.source.y };
        const already = events.some((e) => (e.type === 'mineExploded' && e.at?.x === at.x && e.at?.y === at.y));
        if (!already) {
            events.push({
                type: 'mineExploded', robotId: hit.source.ownerId ?? '', at, count: MINE_DAMAGE,
                kind: hit.source.kind, tokenId: hit.source.id,
                squares: blastSquares(grid, at.x, at.y, MINE_DAMAGE, { register }),
            });
        }
        const victim = bystanders?.find((r) => r.id === hit.robotId);
        if (!victim)
            continue;
        // The robot that set it off is standing in it. Its own move is over.
        if (hurt(victim, hit.damage, events, { type: 'mineDamage' })
            && victim.id === mover.id)
            moverDied = true;
    }
    return moverDied;
}
/**
 * Everything the square a robot has just arrived in does to it.
 *
 * Extracted so there is ONE copy. It was inline in `attemptOneSquare`, so
 * a mover that did not go through that function -- a stunt ramp launch,
 * which flies the robot straight to its landing square -- applied none of
 * it: no entry report, so no mine, debris or goo; no spikes; no speed
 * bump; no flamethrower. This is the recurring shape of the project's
 * bugs, a consequence wired to one CAUSE rather than to the EVENT, so the
 * fix is to make every mover able to call the same step.
 *
 * The robot must ALREADY be standing in the square: the caller has moved
 * it and emitted its `moved` (or `launched`) event, and anything that
 * damages on arrival belongs below that. Fall damage is NOT here -- it
 * belongs to the crossing rather than to the square, and the two movers
 * compute it differently.
 */
export function applyArrivalEffects(grid, mover, events, 
/** Absent outside Robot Movement, which is what switches off the
 * flamethrower's entry burn. */
register, 
/** What the CARD was worth, for speed bumps. */
moveValue, 
/** The dropped devices on the board, so a spike token cuts on exactly
 * the occasions a printed spiked floor does. Absent from a caller with
 * no token layer to spend from -- the tests, mostly. */
devices, 
/** What moved the robot here, which decides whether a proximity mine a
 * square away senses it. See `BOARD_MOVED`. */
cause = 'card', 
/** Everyone on the board. A mine goes off as the robot ARRIVES, and a
 * blast catches whoever is near it, so the bystanders have to be
 * reachable from here. Without it the trigger is still recorded and
 * spent, but nothing goes off. */
robots, 
/** The register a raised BOLLARD is judged against, which is not the
 * same question as what lights a flamethrower -- see `attemptOneSquare`,
 * where the two were split for the same reason. Board Elements Move
 * passes one and not the other: a bollard that is up is a wall, and it
 * shelters whoever is behind it from a blast, on every step of the
 * register. Defaults to `register`, which is right for every caller
 * that has only the one. */
edgeRegister = register) {
    const robotId = mover.id;
    const nx = mover.x;
    const ny = mover.y;
    const arrived = grid.cells[ny][nx];
    const absorb = (absorbed) => events.push({
        type: 'damageAbsorbed', robotId, count: absorbed,
    });
    // Every entry is reported, so debris can answer for it without the
    // movement code needing to know it exists.
    events.push({ type: 'enteredSquare', robotId, at: { x: nx, y: ny } });
    // A mine and a proximity mine answer to the STEP, here, whatever put
    // the robot on the square -- with the spike. The device goes off as the
    // robot ARRIVES, not after the card has finished: the blast is measured
    // against the board as it stands at this instant, and a robot it
    // destroys stops here.
    if (detonateSteppedOn(grid, mover, events, devices, robots?.values(), !BOARD_MOVED.has(cause), edgeRegister))
        return 'destroyed';
    // Spikes: "A robot entering or rotating on a square with a spiked
    // floor will receive a point of damage." Distinct from a spiked WALL,
    // which is an edge feature and damages on collision.
    //
    // The printed floor and a DROPPED SPIKE are two different things -- --
    // and this is where they meet: the floor is a property of the square
    // and cuts everyone who ever enters, the token is an object lying on it
    // and is destroyed by the damage it causes. Same point of damage, same
    // event, same moment.
    //
    // The floor is checked FIRST and short-circuits, so a spike token lying
    // on printed spikes is not spent by a cut the board was going to make
    // anyway: a token is destroyed by the damage IT causes.
    if (hasTerrain(arrived, 'spikes') || devices?.spike(nx, ny)) {
        if (hurt(mover, 1, events, {
            type: 'spikeFloorDamage', at: { x: nx, y: ny },
        }))
            return 'destroyed';
    }
    // Speed bump: "A robot that moves or is pushed onto a speed bump with a
    // Move 2 or Move 3 receives a point of damage." A Move 1, a Back Up and
    // a rotate never trigger it, however the robot got there.
    if (hasTerrain(arrived, 'speedBump') && (moveValue ?? 0) >= 2) {
        if (hurt(mover, 1, events, { type: 'speedBumpDamage' }))
            return 'destroyed';
    }
    if (register !== undefined && arrived.flamer?.phases.includes(register)) {
        if (hurt(mover, 1, events, { type: 'flamerBurn' }))
            return 'destroyed';
    }
    // Goo catches the robot HERE, in the middle of the move that brought it
    // in. "If a robot passes over or stops on the goo, the robot cannot
    // leave that square until it attempts to move a total of four squares."
    //
    // PASSES OVER is the word that matters, and it was not implemented: goo
    // was picked up only by `resolveGoo` after the whole card had run, so a
    // Move 2 that entered goo on its first square sailed out the other side
    // on its second and was never stuck at all -- no count, no log line,
    // nothing. From a Move 2 into goo that carried on into a repulsor.
    //
    // Reported as `stuck` rather than damage, and the caller ends the move.
    // A robot ALREADY stuck in this same goo is not re-caught: it is
    // struggling, and the struggle is charged in the move loop.
    //
    // Read off the LAYER rather than the square: goo is a token. The timing
    // is unchanged -- this is still the moment the robot arrives, which is
    // what lets the move stop here -- only the place the answer comes from.
    const goo = devices?.goo(nx, ny);
    if (goo && mover.stuckIn?.tokenId !== goo.tokenId) {
        mover.stuckIn = { tokenId: goo.tokenId, attempted: goo.struggled };
        events.push({
            type: 'stuckInGoo',
            robotId,
            tokenId: goo.tokenId,
            count: GOO_STRUGGLE - goo.struggled,
        });
        return 'stuck';
    }
    return 'ok';
}
/**
 * Resolves a robot's Move/Back-Up card: `squares` of movement budget, one
 * square attempted at a time in `dir`, stopping early (without consuming
 * remaining budget) the moment a step is blocked. Ramp crossings consume
 * more than 1 square of budget -- if the remaining budget can't cover a
 * ramp's full cost, movement stops there with zero further progress
 * (confirmed: never a slide-back, see the module docstring). Does not yet
 * apply the oil/mud/sand pre-adjustments to `squares` itself -- see
 * `terrain.ts`, which computes the adjusted `squares` before calling this.
 *
 * Water is handled here directly, not in terrain.ts, because unlike the
 * other terrain effects it isn't a starting-square-only rule -- it can
 * trigger on ANY square left during this card's movement, so it has to be
 * checked at every step of the loop, not just before it starts. Capped at
 * once per register: pass `waterNegationAlreadyUsed` from the previous
 * card's result to carry that forward, and reset it to false yourself at
 * the start of each new register. Currents are exempt even though every
 * current tile is also water.
 *
 * Sand's enter-mid-move rule is here for the same reason: "a Move-3 that
 * enters sand stops in the first square of sand" (confirmed verbatim
 * against tiles.yml) depends on which square was just entered, not known
 * until the loop runs. Pass `stopOnEnteringSand: true` only when resolving
 * a Move-3 card -- this is NOT the starting-square rule (that one is a full
 * block, handled by `terrain.ts`'s `adjustForStartingTerrain` before this
 * function is ever called); this is specifically about sand reached
 * partway through the move.
 */
export function resolveRobotMove(grid, robots, robotId, dir, squares, options = {}) {
    const working = new Map(robots.map((r) => [r.id, { ...r }]));
    const events = [];
    let waterUsed = options.waterNegationAlreadyUsed ?? false;
    let rampLaunchSquares;
    // Scoped to ONE card's movement, which is exactly the span the rule
    // describes: both squares of a two-square push come from the same card.
    // Created here rather than threaded through the register for that
    // reason -- unlike `repulsorsFired`, which must survive the whole
    // register and is passed in.
    const rampPushProgress = options.rampPushProgress ?? new Map();
    // Ramps this movement has already paid for. Same scope as the progress
    // map: one card, which is the span in which a pusher follows the robot
    // it shoved up.
    const rampPaid = new Set();
    // Ramming Gear is once per card per victim; this is that card.
    const rammed = new Set();
    // Hydraulic Bumpers take one square off a push, and a card's shove --
    // however many squares it runs to, and however long the chain it drives
    // -- is one push. Same scope as `rammed` for the same reason.
    const bumped = new Set();
    // Pit Move turns each robot once a card, so this card keeps the list.
    const pushWith = options.pushWith?.effect === 'pitMove'
        ? { effect: 'pitMove', done: new Set() }
        : options.pushWith;
    let budget = squares;
    while (budget > 0) {
        const mover = working.get(robotId);
        if (!mover || mover.destroyed)
            break;
        // A MOVEMENT CARD only: an Option's shove does not pay this. A Big
        // Gun fired from water recoils the full square, and so does a pressor
        // or tractor beam, a mini-howitzer, or another robot's push. That
        // falls out of where the check sits rather than being tested for:
        // this is the card's budget loop, and a shove goes through
        // `attemptOneSquare` instead, which has no budget for the square to
        // come out of.
        if (!waterUsed && !options.ignoresTerrain) {
            const currentCell = grid.cells[mover.y][mover.x];
            // A current on the square does NOT exempt it. What is exempt is
            // movement BY a current, which never reaches this loop at all --
            // `resolveConveyance` sets coordinates directly. Testing the square
            // instead of the mover meant a Movement card leaving water was
            // negated only where the water carried no current, and on Water
            // Park almost every water square carries one, so the rule barely
            // fired on the board built around it.
            if (hasTerrain(currentCell, 'water')) {
                waterUsed = true;
                events.push({ type: 'waterNegation', robotId });
                budget -= 1;
                if (budget <= 0)
                    break;
            }
        }
        // Standing on a ramp and about to leave over its exit: the ordinary
        // step is replaced by a launch, which the caller performs.
        const rampHere = grid.cells[mover.y][mover.x].stuntRamp;
        if (rampHere && rampHere.exit === dir) {
            rampLaunchSquares = budget;
            break;
        }
        const edge = getEdgeCrossing(grid, mover.x, mover.y, dir, options.register, canClimb(mover));
        // A ramp this movement already paid for costs one square, not its
        // full climb: the squares spent shoving a robot up carried this one
        // over too.
        const cost = rampPaid.has(`${mover.x},${mover.y},${dir}`) ? 1 : edge.cost;
        if (edge.blocked) {
            if (edge.spiked) {
                if (hurt(mover, 1, events, { type: 'spikeDamage' }))
                    break;
            }
            events.push({ type: 'blocked', robotId });
            break;
        }
        // Goo, handled HERE rather than left to `attemptOneSquare`, because a
        // stuck robot is not crossing the edge and the edge's price is
        // therefore not its price. Two things follow.
        //
        // A ramp too dear to climb no longer ends the move silently. "since a
        // robot moves partially up a ramp on a 1 or backup, it's still
        // movement, which should remove 1 count of goo." The `cost > budget`
        // break below sat above the goo check inside `attemptOneSquare`, so a
        // Move 1 or a Back Up into a two-square climb spent nothing at all.
        //
        // And a square of struggling costs ONE square of the card, not the
        // climb it is not making. A Move 3 is three squares of struggling
        // whatever it is facing.
        //
        // A WALL still ends the move without struggling: `edge.blocked` above
        // returns first, and the ruling above turns on the robot moving
        // partially, which into a wall it does not.
        if (mover.stuckIn) {
            const attempted = mover.stuckIn.attempted + 1;
            if (attempted >= GOO_STRUGGLE) {
                // The square that breaks it free is spent breaking free rather
                // than travelling -- but ONLY that square. Whatever the card has
                // left then moves the robot normally.
                //
                // Superseding 2026-08-18 ("the robot does not move that register,
                // even with movement left on the card"): "once the goo is
                // destroyed the robot may move normally, whether they started
                // movement in the square or moved into the square mid turn." So a
                // Move 3 into a patch with one square left goes in, breaks it,
                // and travels one more.
                //
                // Which is why this spends a square and CONTINUES rather than
                // breaking out. The old behaviour is gone, not gated.
                events.push({
                    type: 'brokeFreeOfGoo', robotId, tokenId: mover.stuckIn.tokenId, at: { x: mover.x, y: mover.y },
                });
                mover.stuckIn = undefined;
                budget -= 1;
                continue;
            }
            mover.stuckIn = { ...mover.stuckIn, attempted };
            events.push({
                type: 'stuckInGoo', robotId, tokenId: mover.stuckIn.tokenId, count: GOO_STRUGGLE - attempted,
            });
            budget -= 1;
            continue;
        }
        if (cost > budget) {
            // Confirmed: insufficient budget for a ramp's full cost just means
            // zero further progress -- no slide-back, no partial credit.
            break;
        }
        const result = attemptOneSquare(grid, working, robotId, dir, events, 'card', options.register, options.moveValue, options.repulsorsFired, rampPushProgress, rampPaid, rammed, bumped, undefined, options.devices, pushWith, options.reengineerWith);
        if (result === 'destroyed')
            break;
        if (result === 'held') {
            // A square of this card was spent getting the robot ahead up a
            // ramp. Nothing moved, but the move is not over: the next square
            // finishes the climb and both robots go.
            budget -= cost;
            continue;
        }
        if (result === 'fell') {
            // The square it fell into is paid for; everything the card had left
            // is not spent. A fall ends the move.
            budget -= cost;
            break;
        }
        if (result === 'blocked') {
            // Goo used to be the exception here. A robot ALREADY stuck is now
            // handled above the cost test instead, and nothing between there
            // and this line can leave a robot in that state, so the exception
            // is gone rather than rewritten. Blocked ends the move.
            break;
        }
        if (result === 'stuckHere') {
            // Drove INTO goo. The square it entered is paid for; the rest of
            // the card is spent struggling in the goo it just landed in, one
            // square at a time, which is what the top of this loop does.
            //
            // Spent rather than lost, so that entering goo and starting the
            // register in goo cost a card the same. INFERRED from the two
            // rulings either side of it rather than confirmed on its own: a
            // robot which enters goo mid-move must stop there, and separately
            // every square a card is worth is a square of struggling.
            budget -= cost;
            continue;
        }
        budget -= cost;
        if (options.stopOnEnteringSand && !options.ignoresTerrain) {
            const landed = working.get(robotId);
            if (landed && !landed.destroyed) {
                const landedCell = grid.cells[landed.y][landed.x];
                if (hasTerrain(landedCell, 'sand')) {
                    events.push({ type: 'enteredSand', robotId });
                    break;
                }
            }
        }
    }
    return {
        robots: [...working.values()], events, waterNegationUsed: waterUsed,
        ...(rampLaunchSquares !== undefined ? { rampLaunchSquares } : {}),
    };
}
